/* ══════════════════════════════
   MEMORY GAME — memory.js
══════════════════════════════ */

import { setupNavigation, cleanupNavigation } from '../UI/navigation.js';
import { langManager } from '../lang/langManager.js';
import {
  awardMedal,
  renderTitleMedals,
  medalModalHTML,
  getNavV,
  getGameSoundEnabled,
  setGameSoundEnabled
} from '../utils/utilsJeux.js';
import {
  initJeuxModal,
  openJeuxModal,
  closeJeuxModal,
  cleanupJeuxModal
} from '../utils/utilsJeux.js';

// ── PAGE ROUTING ───────────────────────────────────────────────
const pageRouteMap = {
  home: '/',
  market: '/market',
  catering: '/catering',
  about: '/about',
  games: '/games',
  boutique: '/boutique'
};

const onNavigateHome = () => {
  window.location.href = pageRouteMap.home;
};
window.addEventListener('navigate-home', onNavigateHome);

const onNavigateSection = e => {
  const target = pageRouteMap[e.detail?.section];
  if (target) window.location.href = target;
};
window.addEventListener('navigate-section', onNavigateSection);

setupNavigation();

/* ══════════════════════════════════════════
   MEMORY GAME — main game logic
══════════════════════════════════════════ */

// ── STATE ──────────────────────────────────────────────────────
let memFlipped = [];
let memMatched = 0;
let memStartTime = null;
let memTimerInterval = null;
let memIsResolving = false;
let memResolveTimer = null;
const memSeen = new Set();

const t = key => langManager.getText(key);

// ── Sound toggle ───────────────────────────────────────────────
let memorySoundEnabled = getGameSoundEnabled();

function initSoundToggle() {
  const btn = document.getElementById('memory-sound-toggle');
  if (!btn) return;
  function update() {
    btn.setAttribute('aria-pressed', String(memorySoundEnabled));
    btn.classList.toggle('muted', !memorySoundEnabled);
    btn.title = memorySoundEnabled ? t('soundOff') : t('soundOn');
  }
  btn.addEventListener('click', () => {
    memorySoundEnabled = !memorySoundEnabled;
    setGameSoundEnabled(memorySoundEnabled);
    update();
  });
  update();
}

// ── Sounds ─────────────────────────────────────────────────────
const memFlipSound = new Audio('/jeux/memory/memFlip.mp3');
memFlipSound.preload = 'auto';
memFlipSound.volume = 0.5;

const memPairSound = new Audio('/jeux/memory/memPair.mp3');
memPairSound.preload = 'auto';
memPairSound.volume = 0.7;

function playMemFlipSound() {
  if (!memorySoundEnabled) return;
  try {
    memFlipSound.currentTime = 0;
    const p = memFlipSound.play();
    if (p?.catch) p.catch(() => {});
  } catch {
    /* ignore */
  }
}

function playMemPairSound() {
  if (!memorySoundEnabled) return;
  try {
    memPairSound.currentTime = 0;
    const p = memPairSound.play();
    if (p?.catch) p.catch(() => {});
  } catch {
    /* ignore */
  }
}

// ── Chrome height measurement ──────────────────────────────────
//
// Grid layout in CSS:
//   portrait  → 4 cols × 6 rows  → grid aspect ratio = cols/rows = 4/6 ≈ 0.667  (wider than it is tall? NO — taller)
//   landscape → 6 cols × 4 rows  → grid aspect ratio = cols/rows = 6/4 = 1.5    (wider than tall)
//
// Given availH (the height the grid can occupy):
//   gridW_from_h = availH * (cols/rows)   ← how wide the grid would be if height-constrained
//   gridW        = min(gridW_from_h, containerW)
//   gridH        = gridW * (rows/cols)    ← actual height once width is known

function updateMemChromeH() {
  try {
    const title = document.querySelector('.page-title');
    const scorebar = document.querySelector('.scorebar');
    const msgEl_ = document.querySelector('.msg');
    const btn = document.querySelector('#mem-start-btn');
    const gameDiv = document.querySelector('.game-div');
    const mainEl = document.querySelector('main');
    const grid = document.getElementById('memory-grid');

    if (!gameDiv || !mainEl || !grid) return;

    // Hide .msg on narrow screens (same as other games)
    const vpW = window.innerWidth;
    if (msgEl_) msgEl_.style.display = vpW <= 768 ? 'none' : '';
    const msgH = msgEl_ && vpW > 768 ? msgEl_.offsetHeight : 0;

    const gameDivGap = parseFloat(getComputedStyle(gameDiv).gap) || 12;
    const mainStyle = getComputedStyle(mainEl);
    const mainPadV = parseFloat(mainStyle.paddingTop) + parseFloat(mainStyle.paddingBottom);
    const mainPadH = parseFloat(mainStyle.paddingLeft) + parseFloat(mainStyle.paddingRight);

    const titleH = title ? title.offsetHeight : 0;
    const scorebarH = scorebar ? scorebar.offsetHeight : 0;
    const btnH = btn ? btn.offsetHeight : 0;

    // Chrome items (everything except the grid)
    const items = [title, scorebar, msgEl_ && vpW > 768 ? msgEl_ : null, btn].filter(Boolean);
    const itemsTotalH = titleH + scorebarH + msgH + btnH;
    // gaps: between chrome items + 1 gap above grid + 1 gap below grid
    const gapsTotalH = gameDivGap * (items.length + 1);
    const navOffset = getNavV();

    const chromeTotal = itemsTotalH + gapsTotalH + mainPadV + navOffset;
    document.documentElement.style.setProperty('--mem-chrome-h', `${chromeTotal}px`);

    // ── Compute exact grid dimensions ──────────────────────────
    // Height the grid can use
    const mainH = mainEl.clientHeight;
    const availH = Math.max(mainH - mainPadV - itemsTotalH - gapsTotalH, 60);

    // Width the grid can use
    const containerW = Math.max(mainEl.clientWidth - mainPadH - 8, 60);

    // Grid shape: portrait = 4 cols / 6 rows, landscape = 6 cols / 4 rows
    const isPortrait = window.innerHeight > window.innerWidth;
    const cols = isPortrait ? 4 : 6;
    const rows = isPortrait ? 6 : 4;

    // Width if we fill the available height exactly
    const wFromH = availH * (cols / rows);

    // Final grid width: constrained by both height and container width
    const gridW = Math.floor(Math.min(wFromH, containerW));
    // Final grid height: derived from the actual width
    const gridH = Math.floor(gridW * (rows / cols));

    grid.style.width = `${gridW}px`;
    grid.style.maxWidth = `${gridW}px`;
    grid.style.height = `${gridH}px`;
    grid.style.maxHeight = `${gridH}px`;
  } catch (err) {
    console.error('[memory layout error]', err);
  }
}

let _memResizeTimer = null;

function scheduleMemLayout(delay = 100) {
  clearTimeout(_memResizeTimer);
  _memResizeTimer = setTimeout(() => requestAnimationFrame(updateMemChromeH), delay);
}

// Boot: double-rAF for first paint, then again after 250 ms for font settle
requestAnimationFrame(() => requestAnimationFrame(updateMemChromeH));
setTimeout(() => scheduleMemLayout(0), 250);

const onMemResize = () => scheduleMemLayout(100);

// orientationchange: browsers (especially iOS Safari) report stale
// innerWidth/innerHeight for up to ~300 ms after the event fires.
const onMemOrientationChange = () => {
  scheduleMemLayout(50);
  scheduleMemLayout(350);
};

window.addEventListener('resize', onMemResize);
window.addEventListener('orientationchange', onMemOrientationChange);

// ── DATA ───────────────────────────────────────────────────────
const MEM_HEADS = [
  'NL_Chef.webp',
  'NL_Clown.webp',
  'NL_Colombo.webp',
  'NL_Elvis.webp',
  'NL_Pecheur.webp',
  'NL_Pirate.webp',
  'NL_Punk.webp',
  'NL_Santa.webp',
  'NL_Thief.webp',
  'NL_Turc.webp',
  'NL_WomCarre.webp',
  'NL_WomFlow.webp'
];

const MEM_MATCH_DELAY_MS = 420;
const MEM_MISMATCH_DELAY_MS = 850;

// ── INIT ───────────────────────────────────────────────────────
function initMemory() {
  clearInterval(memTimerInterval);
  renderTitleMedals('memory');
  memStartTime = null;
  memFlipped = [];
  memIsResolving = false;
  if (memResolveTimer) {
    clearTimeout(memResolveTimer);
    memResolveTimer = null;
  }
  memSeen.clear();
  memMatched = 0;

  document.getElementById('mem-moves').textContent = '0s';
  document.getElementById('mem-pairs').textContent = 0;

  const msg = document.getElementById('mem-msg');
  msg.textContent = t('memPrompt');
  msg.className = 'msg';

  const btn = document.getElementById('mem-start-btn');
  if (btn) btn.textContent = t('play');

  const grid = document.getElementById('memory-grid');
  grid.innerHTML = '';

  const shuffled = [...MEM_HEADS, ...MEM_HEADS].sort(() => Math.random() - 0.5);
  shuffled.forEach((head, i) => {
    const card = document.createElement('div');
    card.className = 'mem-card';
    card.dataset.emoji = head;
    card.dataset.index = i;
    card.innerHTML = `
      <div class="face"></div>
      <div class="back"><img src="/jeux/heads/${head}" alt="${head}" loading="lazy" draggable="false"/></div>`;
    card.addEventListener('click', () => flipCard(card));
    grid.appendChild(card);
  });
}

// ── TIMER ──────────────────────────────────────────────────────
function startTimer() {
  memStartTime = Date.now();
  memTimerInterval = setInterval(() => {
    document.getElementById('mem-moves').textContent = formatTime(Date.now() - memStartTime);
  }, 1000);
}

// ── FLIP ───────────────────────────────────────────────────────
function flipCard(card) {
  if (card.classList.contains('flipped') || card.classList.contains('matched')) return;
  if (memIsResolving && memFlipped.length === 2) resolveCardsImmediately();
  if (memIsResolving) return;
  if (!memStartTime) startTimer();

  playMemFlipSound();
  card.classList.add('flipped');
  memFlipped.push(card);
  if (memFlipped.length === 2) checkMatch();
}

function checkMatch() {
  memIsResolving = true;
  const [a, b] = memFlipped;
  const isMatch = a.dataset.emoji === b.dataset.emoji;

  if (isMatch) {
    memMatched++;
    document.getElementById('mem-pairs').textContent = memMatched;
    playMemPairSound();
    memResolveTimer = setTimeout(() => {
      memResolveTimer = null;
      if (memFlipped.includes(a)) {
        a.classList.add('matched');
        b.classList.add('matched');
        memFlipped = [];
        memIsResolving = false;
        if (memMatched === MEM_HEADS.length) showWin();
      } else {
        memIsResolving = false;
      }
    }, MEM_MATCH_DELAY_MS);
  } else {
    memSeen.add(Number(a.dataset.index));
    memSeen.add(Number(b.dataset.index));
    memResolveTimer = setTimeout(() => {
      memResolveTimer = null;
      if (memFlipped.length === 2 && memFlipped.includes(a)) {
        a.classList.remove('flipped');
        b.classList.remove('flipped');
        memFlipped = [];
        memIsResolving = false;
      } else {
        memIsResolving = false;
      }
    }, MEM_MISMATCH_DELAY_MS);
  }
}

function resolveCardsImmediately() {
  if (memResolveTimer) {
    clearTimeout(memResolveTimer);
    memResolveTimer = null;
  }
  const [a, b] = memFlipped;
  if (!a || !b) {
    memIsResolving = false;
    memFlipped = [];
    return;
  }
  if (a.dataset.emoji === b.dataset.emoji) {
    a.classList.add('matched');
    b.classList.add('matched');
    if (memMatched === MEM_HEADS.length) showWin();
  } else {
    a.classList.remove('flipped');
    b.classList.remove('flipped');
  }
  memFlipped = [];
  memIsResolving = false;
}

// ── HELPERS ────────────────────────────────────────────────────
function formatTime(ms) {
  const s = Math.floor(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

// ── WIN MODAL ──────────────────────────────────────────────────
function showWin() {
  clearInterval(memTimerInterval);
  const elapsed = Date.now() - memStartTime;
  const secs = elapsed / 1000;
  const earnedMedal = secs < 35 ? 1 : secs < 42 ? 2 : secs < 50 ? 3 : null;
  const timeStr = formatTime(elapsed);
  const bestMedal = awardMedal('memory', earnedMedal);
  renderTitleMedals('memory');

  document.getElementById('jeux-modal-body').innerHTML = `
    <div class="end-title">${t('youWin')}</div>
    <div class="end-stats">
      <div class="end-stat">
        <div class="end-stat-val">${timeStr}</div>
        <div class="end-stat-lbl">${t('time')}</div>
      </div>
    </div>
    ${medalModalHTML(earnedMedal, bestMedal, t)}`;

  setTimeout(() => openJeuxModal(), 600);

  document.getElementById('mem-msg').textContent = t('memPrompt');
  document.getElementById('mem-msg').className = 'msg';
  const btn = document.getElementById('mem-start-btn');
  if (btn) btn.textContent = t('play');
}

function restartMemory() {
  closeJeuxModal();
  document.getElementById('mem-msg').textContent = t('memPrompt');
  document.getElementById('mem-msg').className = 'msg';
  initMemory();
}

// ── Start button ───────────────────────────────────────────────
const memStartBtn = document.getElementById('mem-start-btn');
if (memStartBtn) {
  memStartBtn.textContent = t('play');
  memStartBtn.addEventListener('click', restartMemory);
}

initMemory();
initSoundToggle();
renderTitleMedals('memory');
initJeuxModal();

// ── CLEANUP ON NAVIGATION ──────────────────────────────────────
window.addEventListener(
  'pagehide',
  () => {
    clearInterval(memTimerInterval);
    memTimerInterval = null;
    memFlipped = [];
    memIsResolving = false;
    if (memResolveTimer) {
      clearTimeout(memResolveTimer);
      memResolveTimer = null;
    }
    memSeen.clear();

    window.removeEventListener('navigate-home', onNavigateHome);
    window.removeEventListener('navigate-section', onNavigateSection);
    window.removeEventListener('resize', onMemResize);
    window.removeEventListener('orientationchange', onMemOrientationChange);
    clearTimeout(_memResizeTimer);

    cleanupNavigation();
    cleanupJeuxModal();
  },
  { once: true }
);
